import { inject, computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { DeliveryNoteData as BaseDeliveryNoteData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';

// Re-export the type for backward compatibility
export type DeliveryNoteWithOrgSiteKey = BaseDeliveryNoteData;
export type DeliveryNoteData = BaseDeliveryNoteData;

export interface DataChangeInfo {
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load';
    data?: DeliveryNoteWithOrgSiteKey;
    timestamp: number;
}

export interface DeliveryNotesState {
    deliveryNotes: DeliveryNoteWithOrgSiteKey[];
    loading: boolean;
    error: string | null;
    lastUpdated: number;
    dataChange: DataChangeInfo;
}

export const initialDeliveryNotesState: DeliveryNotesState = {
    deliveryNotes: [],
    loading: false,
    error: null,
    lastUpdated: 0,
    dataChange: {
        type: 'refresh',
        data: undefined,
        timestamp: 0,
    },
};

export const DeliveryNotesStore = signalStore(
    { providedIn: 'root' },
    withState<DeliveryNotesState>(initialDeliveryNotesState),
    withComputed(({ deliveryNotes }) => ({
        deliveryNotesCount: computed(() => deliveryNotes().length),
        filteredDeliveryNotes: computed(() => deliveryNotes()),
    })),
    withMethods((store, searchStore = inject(SearchStore)) => {
        const triggerDataChange = (
            type: DataChangeInfo['type'],
            data?: DeliveryNoteWithOrgSiteKey
        ) => {
            patchState(store, {
                dataChange: { type, data, timestamp: Date.now() },
            });
        };

        return {
            // --- Pagination delegated to SearchStore ---
            getTotalCount(): number {
                return searchStore.pagination().totalCount;
            },
            getHasMore(): boolean {
                return searchStore.pagination().hasMore;
            },
            getLimit(): number {
                return searchStore.pagination().limit;
            },
            getOffset(): number {
                return searchStore.pagination().offset;
            },

            // --- Actions ---
            setLoading(loading: boolean): void {
                patchState(store, { loading });
            },
            setError(error: string | null): void {
                patchState(store, { error });
            },
            setDeliveryNotes(deliveryNotes: DeliveryNoteWithOrgSiteKey[]): void {
                patchState(store, { deliveryNotes, lastUpdated: Date.now() });
                triggerDataChange('load');
            },
            addDeliveryNote(deliveryNote: DeliveryNoteWithOrgSiteKey): void {
                patchState(store, state => ({
                    deliveryNotes: [...state.deliveryNotes, deliveryNote],
                    lastUpdated: Date.now(),
                }));
                triggerDataChange('add', deliveryNote);
            },
            updateDeliveryNote(updatedNote: DeliveryNoteWithOrgSiteKey): void {
                patchState(store, state => ({
                    deliveryNotes: state.deliveryNotes.map(note =>
                        note._key === updatedNote._key ? updatedNote : note
                    ),
                    lastUpdated: Date.now(),
                }));
                triggerDataChange('update', updatedNote);
            },
            removeDeliveryNote(noteKey: string): void {
                const noteToRemove = store.deliveryNotes().find(
                    note => note._key === noteKey
                );
                patchState(store, state => ({
                    deliveryNotes: state.deliveryNotes.filter(
                        note => note._key !== noteKey
                    ),
                    lastUpdated: Date.now(),
                }));
                if (noteToRemove) {
                    triggerDataChange('delete', noteToRemove);
                }
            },
            clearDeliveryNotes(): void {
                patchState(store, {
                    deliveryNotes: [],
                    lastUpdated: Date.now(),
                });
                triggerDataChange('refresh');
            },
            refreshDeliveryNotes(): void {
                triggerDataChange('refresh');
            },
            triggerDataChange,

            // Optimistic operations (delegate to core methods)
            optimisticUpdateDeliveryNote(
                updatedNote: DeliveryNoteWithOrgSiteKey
            ): void {
                this.updateDeliveryNote(updatedNote);
            },
            optimisticAddDeliveryNote(
                deliveryNote: DeliveryNoteWithOrgSiteKey
            ): void {
                this.addDeliveryNote(deliveryNote);
            },
            optimisticRemoveDeliveryNote(noteKey: string): void {
                this.removeDeliveryNote(noteKey);
            },

            // Bulk operations
            bulkUpdateDeliveryNotes(
                updatedNotes: DeliveryNoteWithOrgSiteKey[]
            ): void {
                patchState(store, state => {
                    const noteMap = new Map(
                        state.deliveryNotes.map(note => [note._key, note])
                    );
                    updatedNotes.forEach(updatedNote => {
                        noteMap.set(updatedNote._key, updatedNote);
                    });
                    return {
                        deliveryNotes: Array.from(noteMap.values()),
                        lastUpdated: Date.now(),
                    };
                });
                triggerDataChange('refresh');
            },
            bulkAddDeliveryNotes(
                newNotes: DeliveryNoteWithOrgSiteKey[]
            ): void {
                patchState(store, state => ({
                    deliveryNotes: [...state.deliveryNotes, ...newNotes],
                    lastUpdated: Date.now(),
                }));
                triggerDataChange('refresh');
            },
        };
    })
);

